<script setup lang="ts">
  import type { PropType } from 'vue'
  import { toRef } from 'vue'
  import { MaterialPauseIcon, MaterialPlayArrowIcon } from '@gohighlevel/ghl-icons/24/material/rounded'
  import { Send01Icon, Trash02Icon, XCloseIcon } from '@gohighlevel/ghl-icons/24/outline'
  import { createElementId } from '@common/core-layer/utils/core.utils'
  import CPButton from '@ui/components/CPButton.vue'
  import CPSpin from '@ui/components/CPSpin.vue'
  import { useVoiceRecorderBarLayout } from '../composables/useVoiceRecorderBarLayout'
  import VoiceWaveform from './VoiceWaveform.vue'
  import type { VoiceRecorderBarLabels, VoiceRecorderStage } from '../types/voice.types'

  const props = defineProps({
    stage: {
      type: String as PropType<VoiceRecorderStage>,
      default: 'idle',
    },
    elapsedMs: {
      type: Number,
      default: 0,
    },
    amplitudes: {
      type: Array as PropType<number[]>,
      default: () => [],
    },
    sending: {
      type: Boolean,
      default: false,
    },
    idPrefix: {
      type: String,
      default: 'voice-recorder',
    },
    confirmAction: {
      type: String as PropType<'send' | 'stop'>,
      default: 'send',
    },
    variant: {
      type: String as PropType<'inline' | 'composer'>,
      default: 'inline',
    },
    /** Compact fits the comment composer row; chat/default keeps original inline chrome. */
    density: {
      type: String as PropType<'default' | 'compact'>,
      default: 'default',
    },
    labels: {
      type: Object as PropType<VoiceRecorderBarLabels>,
      required: true,
    },
  })

  const emit = defineEmits<{
    (e: 'cancel'): void
    (e: 'togglePause'): void
    (e: 'confirm'): void
  }>()

  const { isPaused, isComposer, isCompact, controlSize, controlIconClass, waveformHeight, durationLabel, pauseLabel, confirmButtonId } =
    useVoiceRecorderBarLayout({
      stage: toRef(props, 'stage'),
      elapsedMs: toRef(props, 'elapsedMs'),
      variant: toRef(props, 'variant'),
      density: toRef(props, 'density'),
      confirmAction: toRef(props, 'confirmAction'),
      idPrefix: toRef(props, 'idPrefix'),
      labels: toRef(props, 'labels'),
    })
</script>

<template>
  <div
    class="voice-recorder-bar"
    :class="{
      'voice-recorder-bar--composer': isComposer,
      'voice-recorder-bar--compact': isCompact,
      'bg-background border border-solid border-default': isComposer,
    }"
    role="group"
    :aria-label="labels.recording"
    :data-variant="variant"
    :data-density="isComposer ? undefined : density"
    :style="isComposer ? undefined : { color: 'var(--cp-primary-500)' }"
  >
    <CPButton
      v-if="!isComposer"
      :id="`${idPrefix}-cancel`"
      type="text"
      :size="controlSize"
      class="voice-recorder-bar__icon-btn voice-recorder-bar__order-cancel"
      :disabled="sending"
      :aria-label="labels.cancel"
      @click="emit('cancel')"
    >
      <Trash02Icon :class="controlIconClass" />
    </CPButton>

    <span
      v-if="!isComposer"
      class="voice-recorder-bar__dot voice-recorder-bar__order-dot"
      :class="{ 'voice-recorder-bar__dot--paused': isPaused }"
    />

    <VoiceWaveform
      class="voice-recorder-bar__waveform voice-recorder-bar__order-waveform"
      live
      :samples="amplitudes"
      :progress="1"
      :height="waveformHeight"
      played-color="currentColor"
      unplayed-color="var(--cp-border-600)"
      :aria-label="labels.waveform"
    />

    <span :id="`${idPrefix}-time`" class="voice-recorder-bar__time voice-recorder-bar__order-time text-800">{{ durationLabel }}</span>

    <!-- eslint-disable-next-line vue/no-restricted-html-elements -- Slack pill pause needs native button; CPButton type=text washes icon color -->
    <button
      v-if="isComposer"
      :id="createElementId(idPrefix, 'toggle', 'pause')"
      type="button"
      class="voice-recorder-bar__composer-pause voice-recorder-bar__order-pause"
      :disabled="sending"
      :aria-label="pauseLabel"
      @click="emit('togglePause')"
    >
      <MaterialPlayArrowIcon v-if="isPaused" class="h-5 w-5" />
      <MaterialPauseIcon v-else class="h-5 w-5" />
    </button>
    <CPButton
      v-else
      :id="createElementId(idPrefix, 'toggle', 'pause')"
      type="text"
      :size="controlSize"
      class="voice-recorder-bar__icon-btn voice-recorder-bar__order-pause"
      :disabled="sending"
      :aria-label="pauseLabel"
      @click="emit('togglePause')"
    >
      <MaterialPlayArrowIcon v-if="isPaused" :class="controlIconClass" />
      <MaterialPauseIcon v-else :class="controlIconClass" />
    </CPButton>

    <CPButton
      v-if="!isComposer"
      :id="confirmButtonId"
      type="primary"
      :size="controlSize"
      class="voice-recorder-bar__icon-btn voice-recorder-bar__send voice-recorder-bar__order-send"
      :disabled="sending"
      :aria-label="labels.confirm"
      @click="emit('confirm')"
    >
      <CPSpin v-if="sending" :id="createElementId(idPrefix, 'send', 'spinner')" size="sm" />
      <Send01Icon v-else :class="controlIconClass" />
    </CPButton>

    <!-- eslint-disable-next-line vue/no-restricted-html-elements -- Slack pill dismiss needs native button; CPButton type=text washes icon color -->
    <button
      v-if="isComposer"
      :id="`${idPrefix}-cancel`"
      type="button"
      class="voice-recorder-bar__dismiss"
      :disabled="sending"
      :aria-label="labels.cancel"
      @click="emit('cancel')"
    >
      <XCloseIcon class="h-3 w-3" />
    </button>
  </div>
</template>

<style scoped>
  .voice-recorder-bar {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    width: 100%;
    padding: 0.375rem 0.5rem;
    color: var(--cp-primary-500);
  }

  .voice-recorder-bar--compact {
    gap: 0.375rem;
    min-height: 1.75rem;
    padding: 0;
  }

  .voice-recorder-bar__order-cancel {
    order: 1;
  }

  .voice-recorder-bar__order-dot {
    order: 2;
  }

  .voice-recorder-bar__order-time {
    order: 3;
  }

  .voice-recorder-bar__order-waveform {
    order: 4;
  }

  .voice-recorder-bar__order-pause {
    order: 5;
  }

  .voice-recorder-bar__order-send {
    order: 6;
  }

  .voice-recorder-bar--composer {
    position: relative;
    box-sizing: border-box;
    width: 14rem;
    min-width: 14rem;
    gap: 0.75rem;
    padding: 0.625rem 1.75rem 0.625rem 0.75rem;
    border-radius: 10px;
    box-shadow: 0 4px 16px var(--cp-shadow);
    color: inherit;
  }

  .voice-recorder-bar--composer .voice-recorder-bar__order-waveform {
    order: 1;
  }

  .voice-recorder-bar--composer .voice-recorder-bar__order-time {
    order: 2;
  }

  .voice-recorder-bar--composer .voice-recorder-bar__order-pause {
    order: 3;
  }

  .voice-recorder-bar__waveform {
    flex: 1 1 auto;
    min-width: 0;
  }

  .voice-recorder-bar--composer .voice-recorder-bar__waveform {
    flex: 1 1 auto;
    min-width: 8rem;
    width: auto;
    align-self: stretch;
  }

  .voice-recorder-bar__time {
    flex: none;
    font-size: 13px;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
  }

  .voice-recorder-bar--compact .voice-recorder-bar__time {
    font-size: 12px;
    line-height: 1;
  }

  .voice-recorder-bar__icon-btn {
    flex: none;
  }

  .voice-recorder-bar--compact .voice-recorder-bar__send {
    width: 1.75rem;
    height: 1.75rem;
    min-width: 1.75rem;
    padding: 0;
  }

  .voice-recorder-bar--compact .voice-recorder-bar__send :deep(.hr-button) {
    width: 1.75rem !important;
    height: 1.75rem !important;
    min-width: 1.75rem !important;
    padding: 0 !important;
  }

  .voice-recorder-bar--compact .voice-recorder-bar__icon-btn :deep(.hr-button) {
    min-height: 1.75rem;
    padding-inline: 0.125rem;
  }

  .voice-recorder-bar__composer-pause {
    flex: none;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1.75rem;
    height: 1.75rem;
    padding: 0;
    margin: 0;
    border: none;
    border-radius: 0;
    cursor: pointer;
    background: transparent;
    color: currentColor;
    line-height: 0;
  }

  .voice-recorder-bar__composer-pause:disabled {
    cursor: default;
    opacity: 0.6;
  }

  .voice-recorder-bar__composer-pause :deep(svg) {
    color: inherit;
    fill: currentColor;
  }

  .voice-recorder-bar__dismiss {
    position: absolute;
    top: -0.4rem;
    right: -0.4rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1.25rem;
    height: 1.25rem;
    padding: 0;
    margin: 0;
    border: none;
    border-radius: 50%;
    cursor: pointer;
    background: var(--cp-font-900);
    color: var(--cp-background-500);
    line-height: 0;
  }

  .voice-recorder-bar__dismiss:disabled {
    cursor: default;
    opacity: 0.6;
  }

  .voice-recorder-bar__dismiss :deep(svg) {
    color: inherit;
    stroke: currentColor;
  }

  .voice-recorder-bar__dot {
    width: 8px;
    height: 8px;
    border-radius: 9999px;
    background: var(--cp-error-500);
    animation: voice-recorder-pulse 1.2s ease-in-out infinite;
  }

  .voice-recorder-bar--compact .voice-recorder-bar__dot {
    width: 6px;
    height: 6px;
  }

  .voice-recorder-bar__dot--paused {
    animation: none;
    opacity: 0.4;
  }

  @keyframes voice-recorder-pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.2;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .voice-recorder-bar__dot {
      animation: none;
    }
  }
</style>
